/**
 * «Surtido» — the files (G2.4, ADR 9010, 9041, 9045, 9007, ADR 9070 R20).
 *
 * Five tables leave as CSV and Excel, one builder each, used twice: by the page's own table (over the
 * rows the planner's filters leave) and by the route's registered export (the whole plan, read to the
 * end), so the file a planner saves from a table and the one the census sees have the same columns,
 * descriptions and provenance.
 *
 *   matrix        the category × peer-group cells, every measure      `preseason.assortment.matrix`
 *   rows          the store × category rows, with the decision        `preseason.assortment.rows`
 *   lines         the store × article lines — NO QUANTITY              `preseason.assortment.lines`
 *   bands         the bands and their cuts                             `preseason.assortment.bands`
 *   placements    every store's group, or the reason it has none       `preseason.assortment.placements`
 *
 * Numbers leave `raw`. A figure the platform does not state is `not-reported`, never 0 (ADR 9061). The
 * Excel shows codes as words (`display`); the CSV keeps the codes. Provenance is stated from the plan —
 * its question, its window, the platform release read from the platform — or declared absent, never
 * invented. A plan has no scenario and no principal in the file, and says so.
 *
 * LIMIT, stated: the lines of a plan are collected page by page before the file is written (the
 * platform also offers the plan as a streamed CSV; a plan of several hundred thousand lines should move
 * to it, as the in-season run cells did — ADR 9073).
 */

import { callOperation, callOperationCsvStream, collectPages, readReasonLabels, reasonDisplay, type CsvProgress, type ReasonLabels } from '@/api';
import {
  assortmentListPlans, assortmentReadPlan, assortmentReadReasonLabels, exportPlan, getPlanWorkbook, listLines, listPlacements, listStoreRows, readStoreMix,
  version as platformVersionOperation,
  type AssortmentMixShareView, type BandView, type ExportPlanParams, type LineView, type MatrixCellView, type PlacementView, type PlanView, type StoreNodeView,
} from '@/api/generated';
import { NOT_PUBLISHED, NO_PRINCIPAL } from '@/export';
import type { ExportCell, ExportColumn, ExportRequest, WorkbookSheet, MeasureCitation, Provenance, ProvenanceAbsent, ProvenanceFilter, Stated } from '@/export';
import { i18next } from '@/i18n';
import { decidedByExportCell, PERSON_NAME_DISPLAY } from '@/planning';
import { exportDisplayOf } from '@/planning/words';

import { COL, type ColName } from './columns';
import { streamedRecords, type PlanRecord } from './csv';
import { fetchNames } from './names';
import { decimal, newestCompleted } from './model';
import { REASON_GROUP, type ReasonGroup } from './reasons';
import { NO_VALUE, readAttributes, readGroup, type AttributeFilter } from './scope';
import { ACTION_KEY, BAND_MEASURE_KEY, BASIS_KEY } from './words';

type ExportKind = NonNullable<NonNullable<ExportPlanParams['query']>['kind']>;

const NOT_REPORTED: ExportCell = { kind: 'missing', state: 'not-reported' };
const PLATFORM_UNREAD: ProvenanceAbsent = { absent: true, reasonKey: 'preseason.assortment.export.platformUnread' };
const NO_SCENARIO: ProvenanceAbsent = { absent: true, reasonKey: 'preseason.assortment.export.noScenario' };
const NOT_REGISTERED = 'not_registered';
/** An empty cell that means «does not apply here» (a band alone has no cluster scheme), as opposed to NOT_REPORTED, «the platform did not say». */
const NOT_APPLICABLE: ExportCell = { kind: 'missing', state: 'not-ranged' };

/** The cluster scheme's status: not applicable to a row compared by band alone (no cluster); not reported where a cluster's status is missing. */
const schemeCell = (clusterId: string | null | undefined, status: string | null | undefined): ExportCell => (
  clusterId === null || clusterId === undefined || clusterId === '' ? NOT_APPLICABLE : code(status)
);

// ── cells ────────────────────────────────────────────────────────────────────

const text = (value: string | null | undefined): ExportCell => (
  typeof value === 'string' && value !== '' ? { kind: 'text', value } : NOT_REPORTED
);
const code = (value: string | null | undefined): ExportCell => (
  typeof value === 'string' && value !== '' ? { kind: 'label', value, code: value } : NOT_REPORTED
);
const count = (value: number | string | null | undefined): ExportCell => {
  const parsed = decimal(value);
  return parsed === null ? NOT_REPORTED : { kind: 'integer', raw: Math.trunc(parsed) };
};
const num = (value: string | number | null | undefined): ExportCell => {
  const parsed = decimal(value);
  return parsed === null ? NOT_REPORTED : { kind: 'number', raw: parsed };
};
const flag = (value: boolean | string | null | undefined): ExportCell => (
  typeof value === 'boolean' || value === 'true' || value === 'false' ? { kind: 'label', value: String(value), code: String(value) } : NOT_REPORTED
);
const day = (value: string | null | undefined): ExportCell => (
  typeof value === 'string' && value !== '' ? { kind: 'date', iso: value.slice(0, 10) } : NOT_REPORTED
);
const money = (value: string | number | null | undefined, currency: string | null): ExportCell => {
  const parsed = decimal(value);
  if (parsed === null) return NOT_REPORTED;
  return currency === null ? { kind: 'number', raw: parsed } : { kind: 'money', raw: parsed, currency };
};

/** The words a boolean cell reads as in the Excel. */
const BOOLEAN_KEY: Readonly<Record<string, string>> = {
  true: 'preseason.assortment.export.yes',
  false: 'preseason.assortment.export.no',
};
const SCHEME_STATUS_KEY: Readonly<Record<string, string>> = {
  proposed: 'preseason.assortment.proposedScheme',
  accepted: 'preseason.assortment.schemeAccepted',
};
const PLACEMENT_STATE_KEY: Readonly<Record<string, string>> = {
  placed: 'preseason.assortment.placementState.placed',
  excluded: 'preseason.assortment.placementState.excluded',
};

/** What a cell may need besides its row: the plan's currency, and the names of the stores and articles the file holds. */
interface Context {
  readonly currency: string | null;
  readonly storeName: (key: string | null | undefined) => string | null;
  readonly articleName: (key: string | null | undefined) => string | null;
}

interface Spec<T> {
  readonly key: ColName;
  readonly kind: ExportColumn['kind'];
  readonly unit?: string;
  readonly display?: ExportColumn['display'];
  /** The platform's reason vocabulary this column's codes belong to: the Excel writes its labels (`reasonDisplay`), the CSV keeps the code. */
  readonly group?: ReasonGroup;
  readonly identifies?: true;
  readonly read: (row: T, context: Context) => ExportCell;
}

function columnsOf<T>(specs: readonly Spec<T>[], currency: string | null, labels: ReasonLabels): readonly ExportColumn[] {
  return specs.map((spec): ExportColumn => {
    const base = {
      key: spec.key,
      // «Not applicable» where a band alone is compared (no scheme to have a status), never the assortment's own «Not ranged».
      ...(spec.key === 'scheme_status' ? { absent: { notRanged: 'notAvailable' as const } } : {}),
      headerKey: COL[spec.key].header,
      descriptionKey: COL[spec.key].help,
      ...(spec.unit === undefined ? {} : { unit: spec.unit }),
      ...(spec.group !== undefined ? { display: reasonDisplay(labels, spec.group) } : spec.display === undefined ? {} : { display: spec.display }),
      ...(spec.identifies === undefined ? {} : { identifies: true as const }),
    };
    if (spec.kind === 'money') return currency === null ? { ...base, kind: 'number' } : { ...base, kind: 'money', currency };
    return { ...base, kind: spec.kind };
  });
}

// ── provenance ───────────────────────────────────────────────────────────────

async function platformRelease(): Promise<Stated<string>> {
  try {
    const release = await callOperation(platformVersionOperation, {});
    return [release.version, `(${release.commitSha.slice(0, 12)})`].join(' ');
  } catch {
    return PLATFORM_UNREAD;
  }
}

const MEASURES: readonly Omit<MeasureCitation, 'registerVersion'>[] = [{
  measure: 'preseason.assortment_plan',
  definition: 'Options carried (breadth) and pairs per option (depth) of a store in a category over the plan window, read against the peers of its band or cluster; the target is the rule\'s figure held to the store\'s own highest breadth in the category; no quantity is proposed.',
}];

/** The plan\'s question as the file\'s provenance states it. Pure of the network but for the platform release. */
export function provenanceOf(plan: PlanView | null, filters: readonly ProvenanceFilter[], platformVersion: Stated<string>): Provenance {
  const from = plan?.windowFirstWeek ?? null;
  const to = plan?.lastWeek ?? null;
  const question: ProvenanceFilter[] = plan === null ? [] : [
    { field: 'country_key', operator: '=', value: plan.countryKey },
    { field: 'hierarchy_level', operator: '=', value: plan.hierarchyLevel },
    { field: 'peer_basis', operator: '=', value: plan.peerBasis },
    { field: 'window_weeks', operator: '=', value: String(plan.windowWeeks) },
  ];
  return {
    filters: [...question, ...filters],
    hierarchyNode: plan === null ? NOT_PUBLISHED : plan.hierarchyLevel,
    timeWindow: from === null || to === null ? NOT_PUBLISHED : { from, to },
    scenario: NO_SCENARIO,
    planVersionId: plan === null ? NOT_PUBLISHED : plan.planId,
    measureDefinitions: MEASURES.map((entry) => ({ ...entry, registerVersion: NOT_REGISTERED })),
    generatedAt: new Date().toISOString(),
    generatedBy: NO_PRINCIPAL,
    platformVersion,
  };
}

/** The names of the stores and articles of `rows`, asked of the plan in chunks, as a context's lookups. */
async function namesOf(planId: string | null, rows: readonly unknown[]): Promise<Pick<Context, 'storeName' | 'articleName'>> {
  const book = { stores: new Map<string, string | null>(), articles: new Map<string, string | null>() };
  if (planId !== null) {
    const stores = new Set<string>();
    const articles = new Set<string>();
    for (const row of rows as readonly { storeKey?: unknown; articleKey?: unknown }[]) {
      if (typeof row.storeKey === 'string') stores.add(row.storeKey);
      if (typeof row.articleKey === 'string') articles.add(row.articleKey);
    }
    await fetchNames(planId, [...stores], [...articles], book);
  }
  return lookups(book);
}

function lookups(book: { readonly stores: ReadonlyMap<string, string | null>; readonly articles: ReadonlyMap<string, string | null> }): Pick<Context, 'storeName' | 'articleName'> {
  return {
    storeName: (key) => (key === null || key === undefined ? null : book.stores.get(key) ?? null),
    articleName: (key) => (key === null || key === undefined ? null : book.articles.get(key) ?? null),
  };
}

interface Shape<T> {
  readonly id: string;
  readonly titleKey: string;
  readonly specs: readonly Spec<T>[];
  readonly plan: PlanView | null;
  readonly filters: readonly ProvenanceFilter[];
  readonly platformVersion: Stated<string>;
  /** The platform's words for the reason codes of the file (the Excel writes them; the CSV keeps the codes). */
  readonly labels: ReasonLabels;
}

/** The platform's words for the plan's reason codes, in the language the file is made in; the code stands where none comes. */
function reasonLabels(): Promise<ReasonLabels> {
  return readReasonLabels(assortmentReadReasonLabels, i18next.language);
}

function shell<T>(args: Shape<T>, rows: ExportRequest['rows'], rowCount?: number): ExportRequest {
  const columns = columnsOf(args.specs, args.plan?.currencyCode ?? null, args.labels);
  return {
    id: args.id,
    titleKey: args.titleKey,
    columns: [...columns],
    rows,
    ...(rowCount === undefined ? {} : { rowCount }),
    provenance: provenanceOf(args.plan, args.filters, args.platformVersion),
    perimeter: {
      filters: [],
      sort: [],
      visibleColumns: columns.map((column) => column.key),
      drillPath: [],
      densityMode: 'compact',
    },
  };
}

/** A file over rows already read (a table's rows, or a small whole read): the names of its keys are asked first. */
async function request<T>(args: Shape<T> & { readonly rows: readonly T[] }): Promise<ExportRequest> {
  const names = await namesOf(args.plan?.planId ?? null, args.rows);
  const context: Context = { currency: args.plan?.currencyCode ?? null, ...names };
  const body: ExportCell[][] = args.rows.map((row) => args.specs.map((spec) => spec.read(row, context)));
  return shell(args, () => body, body.length);
}

/** The narrowing of the address as the platform's file takes it, and as the file's provenance states it. */
function attributeFilters(attrs: AttributeFilter): { readonly query: { readonly gender?: string; readonly brand?: string }; readonly provenance: readonly ProvenanceFilter[] } {
  return {
    query: { ...(attrs.gender === null ? {} : { gender: attrs.gender }), ...(attrs.brand === null ? {} : { brand: attrs.brand }) },
    provenance: [
      ...(attrs.gender === null ? [] : [{ field: 'gender', operator: '=', value: attrs.gender }]),
      ...(attrs.brand === null ? [] : [{ field: 'brand', operator: '=', value: attrs.brand }]),
    ],
  };
}

/** The records held before the names of their keys are asked. */
const NAMING_BATCH = 500;

type StreamQuery = {
  readonly node_id?: string;
  readonly store_key?: string;
  readonly decision?: 'accept' | 'reject' | 'undecided';
  readonly flag?: 'under_assorted' | 'over_assorted' | 'missing' | 'bounded' | 'refused';
  readonly gender?: string;
  readonly brand?: string;
};

/**
 * The rows of a file read from the platform's own streamed CSV (`…/export?kind=`): each record is read as it arrives,
 * its store and article named in batches of the names cap, and handed to the writer. The plan is never one string or
 * one array. Each read of `rows()` opens the stream again (the Excel writer reads twice), and a file that comes up
 * short throws at its end.
 */
function streamedRows<T>(
  plan: PlanView | null, kind: ExportKind, query: StreamQuery, specs: readonly Spec<T>[], onProgress?: CsvProgress,
): () => AsyncIterable<readonly ExportCell[]> {
  const planId = plan?.planId ?? null;
  return () => (async function* read() {
    if (planId === null) return;
    const file = await callOperationCsvStream(exportPlan, { path: { plan_id: planId }, query: { kind, ...query } });
    const book = { stores: new Map<string, string | null>(), articles: new Map<string, string | null>() };
    const context: Context = { currency: plan?.currencyCode ?? null, ...lookups(book) };
    let held: PlanRecord[] = [];
    const settle = async function* settleHeld(): AsyncGenerator<readonly ExportCell[]> {
      const stores = new Set<string>();
      const articles = new Set<string>();
      for (const record of held) {
        const store = record['storeKey'];
        const article = record['articleKey'];
        if (typeof store === 'string' && !book.stores.has(store)) stores.add(store);
        if (typeof article === 'string' && !book.articles.has(article)) articles.add(article);
      }
      if (stores.size > 0 || articles.size > 0) await fetchNames(planId, [...stores], [...articles], book);
      for (const record of held) yield specs.map((spec) => spec.read(record as unknown as T, context));
      held = [];
    };
    for await (const record of streamedRecords(file, onProgress)) {
      held.push(record);
      if (held.length >= NAMING_BATCH) yield* settle();
    }
    yield* settle();
  })();
}

// ── the columns of each table ────────────────────────────────────────────────

const yesNo = exportDisplayOf(BOOLEAN_KEY);

/** A gender or a brand in the Excel: the master's own spelling, and «Not stated» for the articles it gives no value for (`~`). */
const attributeDisplay: NonNullable<ExportColumn['display']> = (value, translate) => (value === NO_VALUE ? translate('preseason.assortment.absent.noAttribute') : value);

const MATRIX_SPECS: readonly Spec<MatrixCellView>[] = [
  { key: 'node_id', kind: 'text', identifies: true, read: (c) => text(c.nodeId) },
  { key: 'node', kind: 'text', read: (c) => text(c.nodeLabel) },
  { key: 'cluster_id', kind: 'text', read: (c) => text(c.clusterId) },
  { key: 'department_family', kind: 'text', read: (c) => text(c.departmentFamily) },
  { key: 'scheme_status', kind: 'label', display: exportDisplayOf(SCHEME_STATUS_KEY), read: (c) => schemeCell(c.clusterId, c.schemeStatus) },
  { key: 'band_index', kind: 'integer', read: (c) => count(c.bandIndex) },
  { key: 'band_low', kind: 'number', read: (c) => num(c.bandLow) },
  { key: 'band_high', kind: 'number', read: (c) => num(c.bandHigh) },
  { key: 'band_measure', kind: 'label', display: exportDisplayOf(BAND_MEASURE_KEY), read: (c) => code(c.bandMeasure) },
  { key: 'stores_in_group', kind: 'integer', unit: 'stores', read: (c) => count(c.storesInGroup) },
  { key: 'stores_carrying', kind: 'integer', unit: 'stores', read: (c) => count(c.storesCarrying) },
  { key: 'median_breadth', kind: 'number', unit: 'models', read: (c) => num(c.medianBreadth) },
  { key: 'upper_quartile_breadth', kind: 'number', unit: 'models', read: (c) => num(c.upperQuartileBreadth) },
  { key: 'median_sales_per_option', kind: 'money', read: (c, x) => money(c.medianSalesPerOption, x.currency) },
  { key: 'units_sold', kind: 'number', unit: 'pairs', read: (c) => num(c.unitsSold) },
  { key: 'sales_value', kind: 'money', read: (c, x) => money(c.salesValue, x.currency) },
  { key: 'margin_value', kind: 'money', read: (c, x) => money(c.marginValue, x.currency) },
  { key: 'under_stores', kind: 'integer', unit: 'stores', read: (c) => count(c.underStores) },
  { key: 'over_stores', kind: 'integer', unit: 'stores', read: (c) => count(c.overStores) },
  { key: 'missing_stores', kind: 'integer', unit: 'stores', read: (c) => count(c.missingStores) },
  { key: 'bounded_stores', kind: 'integer', unit: 'stores', read: (c) => count(c.boundedStores) },
  { key: 'tail_options', kind: 'integer', unit: 'models', read: (c) => count(c.tailOptions) },
  { key: 'added_now', kind: 'integer', unit: 'models', read: (c) => count(c.addedNow) },
  { key: 'added_next_buy', kind: 'integer', unit: 'models', read: (c) => count(c.addedNextBuy) },
  { key: 'removed', kind: 'integer', unit: 'models', read: (c) => count(c.removed) },
  { key: 'baseline_gap', kind: 'label', group: REASON_GROUP.flag, read: (c) => code(c.baselineGap) },
];

const ROW_SPECS: readonly Spec<StoreNodeView>[] = [
  { key: 'store_key', kind: 'text', identifies: true, read: (r) => text(r.storeKey) },
  { key: 'store_name', kind: 'text', read: (r, x) => text(x.storeName(r.storeKey)) },
  { key: 'node_id', kind: 'text', identifies: true, read: (r) => text(r.nodeId) },
  { key: 'node', kind: 'text', read: (r) => text(r.nodeLabel) },
  { key: 'cluster_id', kind: 'text', read: (r) => text(r.clusterId) },
  { key: 'department_family', kind: 'text', read: (r) => text(r.departmentFamily) },
  { key: 'scheme_status', kind: 'label', display: exportDisplayOf(SCHEME_STATUS_KEY), read: (r) => schemeCell(r.clusterId, r.schemeStatus) },
  { key: 'band_index', kind: 'integer', read: (r) => count(r.bandIndex) },
  { key: 'breadth', kind: 'integer', unit: 'models', read: (r) => count(r.breadth) },
  { key: 'depth', kind: 'number', unit: 'pairs per model', read: (r) => num(r.depth) },
  { key: 'target', kind: 'integer', unit: 'models', read: (r) => count(r.target) },
  { key: 'unbounded_target', kind: 'integer', unit: 'models', read: (r) => count(r.unboundedTarget) },
  { key: 'room', kind: 'integer', unit: 'models', read: (r) => count(r.room) },
  { key: 'bounded', kind: 'label', display: yesNo, read: (r) => flag(r.bounded) },
  { key: 'delta', kind: 'integer', unit: 'models', read: (r) => count(r.delta) },
  { key: 'added_now', kind: 'integer', unit: 'models', read: (r) => count(r.addedNow) },
  { key: 'added_next_buy', kind: 'integer', unit: 'models', read: (r) => count(r.addedNextBuy) },
  { key: 'removed', kind: 'integer', unit: 'models', read: (r) => count(r.removed) },
  { key: 'kept', kind: 'integer', unit: 'models', read: (r) => count(r.kept) },
  { key: 'gap', kind: 'integer', unit: 'models', read: (r) => count(r.gap) },
  { key: 'gap_reason', kind: 'label', group: REASON_GROUP.gap, read: (r) => code(r.gapReason) },
  { key: 'target_rule', kind: 'label', group: REASON_GROUP.rule, read: (r) => code(r.targetRule) },
  { key: 'under_assorted', kind: 'label', display: yesNo, read: (r) => flag(r.underAssorted) },
  { key: 'over_assorted', kind: 'label', display: yesNo, read: (r) => flag(r.overAssorted) },
  { key: 'missing', kind: 'label', display: yesNo, read: (r) => flag(r.missing) },
  { key: 'missing_reason', kind: 'label', group: REASON_GROUP.flag, read: (r) => code(r.missingReason) },
  { key: 'refusal', kind: 'label', group: REASON_GROUP.flag, read: (r) => code(r.refusal) },
  { key: 'recent_options', kind: 'integer', unit: 'models', read: (r) => count(r.recentOptions) },
  { key: 'tail_options', kind: 'integer', unit: 'models', read: (r) => count(r.tailOptions) },
  { key: 'units_sold', kind: 'number', unit: 'pairs', read: (r) => num(r.unitsSold) },
  { key: 'sales_value', kind: 'money', read: (r, x) => money(r.salesValue, x.currency) },
  { key: 'margin_value', kind: 'money', read: (r, x) => money(r.marginValue, x.currency) },
  { key: 'sales_per_option', kind: 'money', read: (r, x) => money(r.salesPerOption, x.currency) },
  { key: 'share_of_store_sales', kind: 'number', read: (r) => num(r.shareOfStoreSales) },
  { key: 'sell_through', kind: 'number', read: (r) => num(r.sellThrough) },
  { key: 'weeks_of_cover', kind: 'number', unit: 'weeks', read: (r) => num(r.weeksOfCover) },
  { key: 'units_on_hand', kind: 'number', unit: 'pairs', read: (r) => num(r.unitsOnHand) },
  { key: 'decision', kind: 'label', display: exportDisplayOf({ accept: 'preseason.assortment.decision.accept', reject: 'preseason.assortment.decision.reject' }), read: (r) => code(r.decision) },
  { key: 'decision_reason', kind: 'label', group: REASON_GROUP.decision, read: (r) => code(r.decisionReason) },
  { key: 'decided_at', kind: 'date', read: (r) => day(r.decidedAt) },
  {
    // Who took the decision, by profile name; absent while nobody has (ADR 9026).
    key: 'decided_by', kind: 'label', display: PERSON_NAME_DISPLAY,
    read: (r) => decidedByExportCell({ disposition: r.decision, actor: r.dispositionActor, actorName: r.dispositionActorName, actorNameAbsentReason: r.dispositionActorNameAbsentReason }),
  },
];

const LINE_SPECS: readonly Spec<LineView>[] = [
  { key: 'line_id', kind: 'text', identifies: true, read: (l) => text(l.lineId) },
  { key: 'store_key', kind: 'text', read: (l) => text(l.storeKey) },
  { key: 'store_name', kind: 'text', read: (l, x) => text(x.storeName(l.storeKey)) },
  { key: 'node_id', kind: 'text', read: (l) => text(l.nodeId) },
  { key: 'node', kind: 'text', read: (l) => text(l.nodeLabel) },
  { key: 'article_key', kind: 'text', read: (l) => text(l.articleKey) },
  { key: 'article_name', kind: 'text', read: (l, x) => text(x.articleName(l.articleKey)) },
  { key: 'gender', kind: 'label', display: attributeDisplay, read: (l) => code(l.gender) },
  { key: 'brand', kind: 'label', display: attributeDisplay, read: (l) => code(l.brand) },
  { key: 'action', kind: 'label', display: exportDisplayOf(ACTION_KEY), read: (l) => code(l.action) },
  { key: 'reason', kind: 'label', group: REASON_GROUP.line, read: (l) => code(l.reason) },
  { key: 'basis', kind: 'label', display: exportDisplayOf(BASIS_KEY), read: (l) => code(l.basis) },
  { key: 'cluster_id', kind: 'text', read: (l) => text(l.clusterId) },
  { key: 'band_index', kind: 'integer', read: (l) => count(l.bandIndex) },
  { key: 'scheme_status', kind: 'label', display: exportDisplayOf(SCHEME_STATUS_KEY), read: (l) => schemeCell(l.clusterId, l.schemeStatus) },
  { key: 'peer_sell_through', kind: 'number', read: (l) => num(l.peerSellThrough) },
  { key: 'peer_units_per_store', kind: 'number', unit: 'pairs', read: (l) => num(l.peerUnitsPerStore) },
  { key: 'units_on_hand', kind: 'number', unit: 'pairs', read: (l) => num(l.unitsOnHand) },
  { key: 'first_seen', kind: 'date', read: (l) => day(l.firstSeen) },
  { key: 'quantity_owner', kind: 'label', display: exportDisplayOf({ initial_allocation: 'preseason.assortment.owner.initial_allocation' }), read: (l) => code(l.quantityOwner) },
  { key: 'quantity_reason', kind: 'label', group: REASON_GROUP.quantity, read: (l) => code(l.quantityReason) },
  { key: 'dc_pairs_reserved', kind: 'number', unit: 'pairs', read: (l) => num(l.dcPairsReserved) },
  { key: 'substitution_rates_supplied', kind: 'label', display: yesNo, read: (l) => flag(l.substitutionRatesSupplied) },
];

const BAND_SPECS: readonly Spec<BandView>[] = [
  { key: 'band_index', kind: 'integer', identifies: true, read: (b) => count(b.bandIndex) },
  { key: 'band_low', kind: 'number', read: (b) => num(b.low) },
  { key: 'band_high', kind: 'number', read: (b) => num(b.high) },
  { key: 'band_measure', kind: 'label', display: exportDisplayOf(BAND_MEASURE_KEY), read: (b) => code(b.measure) },
  { key: 'stores', kind: 'integer', unit: 'stores', read: (b) => count(b.stores) },
];

const PLACEMENT_SPECS: readonly Spec<PlacementView>[] = [
  { key: 'store_key', kind: 'text', identifies: true, read: (p) => text(p.storeKey) },
  { key: 'store_name', kind: 'text', read: (p, x) => text(x.storeName(p.storeKey)) },
  { key: 'placement_state', kind: 'label', display: exportDisplayOf(PLACEMENT_STATE_KEY), read: (p) => code(p.state) },
  { key: 'band_index', kind: 'integer', read: (p) => count(p.bandIndex) },
  { key: 'cluster_id', kind: 'text', read: (p) => text(p.clusterId) },
  { key: 'department_family', kind: 'text', read: (p) => text(p.familyKey) },
  { key: 'scheme_status', kind: 'label', display: exportDisplayOf(SCHEME_STATUS_KEY), read: (p) => schemeCell(p.clusterId, p.schemeStatus) },
  { key: 'volume_units', kind: 'number', unit: 'pairs', read: (p) => num(p.volumeUnits) },
  { key: 'placement_reason', kind: 'label', group: REASON_GROUP.placement, read: (p) => code(p.reason) },
];

/** One value's part of a store's mix, in the dimension it belongs to. */
export interface MixRow {
  readonly attribute: 'gender' | 'brand';
  readonly share: AssortmentMixShareView;
}

const MIX_ATTRIBUTE_KEY: Readonly<Record<string, string>> = {
  gender: 'preseason.assortment.attr.gender',
  brand: 'preseason.assortment.attr.brand',
};

const MIX_SPECS: readonly Spec<MixRow>[] = [
  { key: 'mix_attribute', kind: 'label', identifies: true, display: exportDisplayOf(MIX_ATTRIBUTE_KEY), read: (m) => code(m.attribute) },
  { key: 'mix_value', kind: 'label', identifies: true, display: attributeDisplay, read: (m) => code(m.share.value) },
  { key: 'mix_options', kind: 'integer', unit: 'models', read: (m) => count(m.share.options) },
  { key: 'mix_options_share', kind: 'number', read: (m) => num(m.share.optionsShare) },
  { key: 'mix_units_sold', kind: 'number', unit: 'pairs', read: (m) => num(m.share.unitsSold) },
  { key: 'mix_units_sold_share', kind: 'number', read: (m) => num(m.share.unitsSoldShare) },
  { key: 'mix_units_on_hand', kind: 'number', unit: 'pairs', read: (m) => num(m.share.unitsOnHand) },
  { key: 'mix_units_on_hand_share', kind: 'number', read: (m) => num(m.share.unitsOnHandShare) },
];

// ── the builders, one per file ───────────────────────────────────────────────

export const MATRIX_EXPORT_ID = 'preseason.assortment.matrix';
export const ROWS_EXPORT_ID = 'preseason.assortment.rows';
export const CELL_ROWS_EXPORT_ID = 'preseason.assortment.cellRows';
export const LINES_EXPORT_ID = 'preseason.assortment.lines';
export const MIX_EXPORT_ID = 'preseason.assortment.mix';
export const BANDS_EXPORT_ID = 'preseason.assortment.bands';
export const PLACEMENTS_EXPORT_ID = 'preseason.assortment.placements';

export async function matrixExportRequest(plan: PlanView | null, cells: readonly MatrixCellView[], filters: readonly ProvenanceFilter[] = []): Promise<ExportRequest> {
  return request({ id: MATRIX_EXPORT_ID, titleKey: 'preseason.assortment.matrix.title', specs: MATRIX_SPECS, rows: cells, plan, filters, platformVersion: await platformRelease(), labels: await reasonLabels() });
}

export async function rowsExportRequest(plan: PlanView | null, rows: readonly StoreNodeView[], filters: readonly ProvenanceFilter[] = [], id: string = ROWS_EXPORT_ID): Promise<ExportRequest> {
  return request({ id, titleKey: 'preseason.assortment.rows.title', specs: ROW_SPECS, rows, plan, filters, platformVersion: await platformRelease(), labels: await reasonLabels() });
}

export async function linesExportRequest(plan: PlanView | null, lines: readonly LineView[], filters: readonly ProvenanceFilter[] = []): Promise<ExportRequest> {
  return request({ id: LINES_EXPORT_ID, titleKey: 'preseason.assortment.lines.title', specs: LINE_SPECS, rows: lines, plan, filters, platformVersion: await platformRelease(), labels: await reasonLabels() });
}

/** A store's mix by gender and by brand, one row per value of each: the table the store page draws. */
export async function mixExportRequest(plan: PlanView | null, rows: readonly MixRow[], filters: readonly ProvenanceFilter[] = []): Promise<ExportRequest> {
  return request({ id: MIX_EXPORT_ID, titleKey: 'preseason.assortment.mix.title', specs: MIX_SPECS, rows, plan, filters, platformVersion: await platformRelease(), labels: await reasonLabels() });
}

export async function bandsExportRequest(plan: PlanView | null, bands: readonly BandView[]): Promise<ExportRequest> {
  return request({ id: BANDS_EXPORT_ID, titleKey: 'preseason.assortment.bands.title', specs: BAND_SPECS, rows: bands, plan, filters: [], platformVersion: await platformRelease(), labels: await reasonLabels() });
}

export async function placementsExportRequest(plan: PlanView | null, placements: readonly PlacementView[], filters: readonly ProvenanceFilter[] = []): Promise<ExportRequest> {
  return request({ id: PLACEMENTS_EXPORT_ID, titleKey: 'preseason.assortment.placements.title', specs: PLACEMENT_SPECS, rows: placements, plan, filters, platformVersion: await platformRelease(), labels: await reasonLabels() });
}

// ── the routes' files: the whole plan the address names, read to the end ─────

/** The plan a route's file is about: the one in the address, else the newest completed. `null`: there is none to export. */
export async function planOfScope(search: string): Promise<PlanView | null> {
  const named = new URLSearchParams(search).get('planRunId');
  if (named !== null && named.trim() !== '') {
    try {
      return await callOperation(assortmentReadPlan, { path: { plan_id: named.trim() } });
    } catch {
      return null;
    }
  }
  try {
    return newestCompleted((await callOperation(assortmentListPlans, { query: { limit: 50 } })).items);
  } catch {
    return null;
  }
}

const WHOLE = 1000;

type Scope = { readonly search: string };

/** `?<key>=` of the address, or `null` when absent or blank. */
function param(scope: Scope, key: string): string | null {
  const value = new URLSearchParams(scope.search).get(key);
  return value === null || value.trim() === '' ? null : value.trim();
}

/** The file of one table of the plan, streamed from the platform's own CSV — the whole plan, never a page. */
async function wholeTable<T>(
  scope: Scope, id: string, titleKey: string, specs: readonly Spec<T>[], kind: ExportKind, query: StreamQuery,
  filters: readonly ProvenanceFilter[] = [],
): Promise<ExportRequest> {
  const plan = await planOfScope(scope.search);
  return shell({ id, titleKey, specs, plan, filters, platformVersion: await platformRelease(), labels: await reasonLabels() }, streamedRows(plan, kind, query, specs));
}

/** What the address narrows a plan's figures by, as the file's provenance states it (a table's own file passes it as `filters`). */
export function attributeProvenance(search: string): readonly ProvenanceFilter[] {
  return attributeFilters(readAttributes(search)).provenance;
}

export function matrixExport(scope: Scope): Promise<ExportRequest> {
  const attrs = attributeFilters(readAttributes(scope.search));
  return wholeTable(scope, MATRIX_EXPORT_ID, 'preseason.assortment.matrix.title', MATRIX_SPECS, 'matrix', attrs.query, attrs.provenance);
}

export function rowsExport(scope: Scope): Promise<ExportRequest> {
  const store = param(scope, 'store');
  const attrs = attributeFilters(readAttributes(scope.search));
  return wholeTable(scope, ROWS_EXPORT_ID, 'preseason.assortment.rows.title', ROW_SPECS, 'rows', { ...(store === null ? {} : { store_key: store }), ...attrs.query },
    [...(store === null ? [] : [{ field: 'store_key', operator: '=', value: store }]), ...attrs.provenance]);
}

/** The stores of one cell of the matrix: the category and the peer group the address names. The platform's file filters by category and store only, so a cell is read by pages (a cell holds the stores of one group). */
export async function cellRowsExport(scope: Scope): Promise<ExportRequest> {
  const plan = await planOfScope(scope.search);
  const node = param(scope, 'node');
  const group = readGroup(scope.search);
  const attrs = attributeFilters(readAttributes(scope.search));
  const query = {
    limit: WHOLE,
    ...attrs.query,
    ...(node === null ? {} : { node_id: node }),
    ...(group === null || group.clusterId === null ? {} : { cluster_id: group.clusterId }),
    ...(group === null || group.bandIndex === null ? {} : { band_index: group.bandIndex }),
  };
  const rows = plan === null ? [] : await collectPages(listStoreRows, { path: { plan_id: plan.planId }, query });
  return rowsExportRequest(plan, rows, Object.entries(query).filter(([key]) => key !== 'limit').map(([field, value]) => ({ field, operator: '=', value: String(value) })), CELL_ROWS_EXPORT_ID);
}

const SHEETS: Readonly<Record<ExportKind, { readonly titleKey: string; readonly specs: readonly Spec<never>[] }>> = {
  matrix: { titleKey: 'preseason.assortment.sheet.matrix', specs: MATRIX_SPECS },
  rows: { titleKey: 'preseason.assortment.sheet.rows', specs: ROW_SPECS },
  lines: { titleKey: 'preseason.assortment.sheet.lines', specs: LINE_SPECS },
  bands: { titleKey: 'preseason.assortment.sheet.bands', specs: BAND_SPECS },
  placements: { titleKey: 'preseason.assortment.sheet.placements', specs: PLACEMENT_SPECS },
};
const DEFAULT_SHEET_ORDER: readonly ExportKind[] = ['matrix', 'rows', 'lines', 'bands', 'placements'];

/**
 * The plan's workbook: its sheets in the order the platform's own workbook read gives (`…/workbook`: one sheet per table,
 * each the read behind it), each streamed from that read. A sheet this build has no columns for is left out, and a read
 * that fails keeps the platform's usual order.
 */
export async function planWorkbook(plan: PlanView | null): Promise<NonNullable<ExportRequest['workbook']>> {
  const labels = await reasonLabels();
  let order = DEFAULT_SHEET_ORDER;
  if (plan !== null) {
    try {
      const book = await callOperation(getPlanWorkbook, { path: { plan_id: plan.planId } });
      const kinds = book.sheets
        .map((sheet) => /[?&]kind=(\w+)/.exec(sheet.path)?.[1])
        .filter((kind): kind is ExportKind => kind !== undefined && Object.prototype.hasOwnProperty.call(SHEETS, kind));
      if (kinds.length > 0) order = kinds;
    } catch {
      // The usual order stands.
    }
  }
  return {
    sheets: order.map((kind): WorkbookSheet => ({
      titleKey: SHEETS[kind].titleKey,
      columns: columnsOf(SHEETS[kind].specs, plan?.currencyCode ?? null, labels),
      rows: streamedRows(plan, kind, {}, SHEETS[kind].specs),
    })),
  };
}

/**
 * The plan's lines. Plan-wide (no filter) the CSV is the lines and the Excel is the plan's whole workbook; narrowed by a
 * store or a category it is those lines, streamed. An action is not a filter the platform's file takes, so that case is read by pages.
 */
export async function linesExport(scope: Scope): Promise<ExportRequest> {
  const store = param(scope, 'store');
  const node = param(scope, 'node');
  const action = param(scope, 'action');
  const attrs = attributeFilters(readAttributes(scope.search));
  const filters: ProvenanceFilter[] = [
    ...(store === null ? [] : [{ field: 'store_key', operator: '=', value: store }]),
    ...(node === null ? [] : [{ field: 'node_id', operator: '=', value: node }]),
    ...(action === null ? [] : [{ field: 'action', operator: '=', value: action }]),
    ...attrs.provenance,
  ];
  if (action !== null) {
    const plan = await planOfScope(scope.search);
    const lines = plan === null ? [] : await collectPages(listLines, {
      path: { plan_id: plan.planId },
      query: { limit: WHOLE, action: action as 'add_now', ...(store === null ? {} : { store_key: store }), ...(node === null ? {} : { node_id: node }), ...attrs.query },
    });
    return linesExportRequest(plan, lines, filters);
  }
  const whole = await wholeTable(scope, LINES_EXPORT_ID, 'preseason.assortment.lines.title', LINE_SPECS, 'lines',
    { ...(store === null ? {} : { store_key: store }), ...(node === null ? {} : { node_id: node }), ...attrs.query }, filters);
  if (filters.length > 0) return whole;
  const plan = await planOfScope(scope.search);
  return { ...whole, workbook: await planWorkbook(plan) };
}

/** The store's mix the address names (`store`, and `node` for one category), both dimensions in one file. */
export async function mixExport(scope: Scope): Promise<ExportRequest> {
  const plan = await planOfScope(scope.search);
  const store = param(scope, 'store');
  const node = param(scope, 'node');
  const mix = plan === null || store === null
    ? null
    : await callOperation(readStoreMix, { path: { plan_id: plan.planId, store_key: store }, query: node === null ? {} : { node_id: node } });
  const rows: readonly MixRow[] = mix === null ? [] : [
    ...mix.byGender.map((share): MixRow => ({ attribute: 'gender', share })),
    ...mix.byBrand.map((share): MixRow => ({ attribute: 'brand', share })),
  ];
  return mixExportRequest(plan, rows, [
    ...(store === null ? [] : [{ field: 'store_key', operator: '=', value: store }]),
    ...(node === null ? [] : [{ field: 'node_id', operator: '=', value: node }]),
  ]);
}

export function bandsExport(scope: Scope): Promise<ExportRequest> {
  return wholeTable(scope, BANDS_EXPORT_ID, 'preseason.assortment.bands.title', BAND_SPECS, 'bands', {});
}

export async function placementsExport(scope: Scope): Promise<ExportRequest> {
  const band = param(scope, 'band');
  if (band === null || !/^\d+$/.test(band)) {
    return wholeTable(scope, PLACEMENTS_EXPORT_ID, 'preseason.assortment.placements.title', PLACEMENT_SPECS, 'placements', {});
  }
  const plan = await planOfScope(scope.search);
  const placements = plan === null ? [] : await collectPages(listPlacements, { path: { plan_id: plan.planId }, query: { limit: WHOLE, band_index: Number(band) } });
  return placementsExportRequest(plan, placements, [{ field: 'band_index', operator: '=', value: band }]);
}
